import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, ShieldCheck } from "lucide-react";
import { AD_POOLS } from "../lib/ads";
import { getNow, MS_DAY } from "../lib/timewarp";
import { hasAnalyticsChoice } from "../lib/consent";

// LocalStorage keys
const LS_FIRST_SEEN = "rl_first_seen_ts_v1"; // number (ms)
const LS_CLOSED_DAYS = "rl_ad_overlay_closed_days_v1"; // { [YYYY-MM-DD]: true }
const LS_VIDEO_WEEKS = "rl_ad_overlay_video_weeks_v1"; // { [YYYY-ww]: true }

// Helper: format date to YYYY-MM-DD (local timezone)
function toDateKeyLocal(ts) {
  const d = new Date(ts);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function startOfDay(ts) {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

// Week numbering: Sunday-start weeks
function getSundayWeekNumber(d) {
  const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  date.setHours(0, 0, 0, 0);
  const yearStart = new Date(d.getFullYear(), 0, 1);
  yearStart.setHours(0, 0, 0, 0);
  const dayOfWeekJan1 = yearStart.getDay(); // 0=Sun..6=Sat
  const startSunday = new Date(yearStart);
  startSunday.setDate(startSunday.getDate() - dayOfWeekJan1); // Sunday on/before Jan 1
  const diffDays = Math.floor((date.getTime() - startSunday.getTime()) / MS_DAY);
  const weekIndex = Math.floor(diffDays / 7) + 1; // 1-based
  return weekIndex;
}

function getWeekKey(d) {
  const wn = getSundayWeekNumber(d);
  return `${d.getFullYear()}-W${String(wn).padStart(2, "0")}`;
}

// Pick an ad for overlay snippet. For video overlay, prefer an emergent ad.
function pickAffiliateAd(seed, preferEmergent = false) {
  // Simple seeded RNG
  let x = (seed || Date.now()) >>> 0;
  const rnd = () => (x = (x * 1664525 + 1013904223) >>> 0);
  const pool = preferEmergent ? AD_POOLS.filter(a => a.emergent) : AD_POOLS;
  if (!pool.length) return null;
  const idx = rnd() % pool.length;
  return pool[idx];
}

export default function AdOverlay() {
  const location = useLocation();
  const [seed, setSeed] = useState(() => Math.floor(Math.random() * 1e9));

  const VIDEO_CLICK_HREF = "https://app.emergent.sh/?via=junian";

  // Videos provided by user
  // Mapping: Video A = Template 4 vertical Final, Video B = Template 5 vertical
  const VIDEO_A_URL = "https://customer-assets.emergentagent.com/job_donation-display-fix/artifacts/n0tn6y1n_Template%204%20vertical%20Final.mp4";
  const VIDEO_B_URL = "https://customer-assets.emergentagent.com/job_donation-display-fix/artifacts/jsahzi5m_Template%205%20vertical.mp4";

  const nowTs = getNow();
  const todayKey = toDateKeyLocal(nowTs);
  const todayDoW = new Date(nowTs).getDay(); // 0=Sun..6=Sat
  const weekKey = getWeekKey(new Date(nowTs));
  const isSaturday = todayDoW === 6;

  // Ensure first-seen timestamp exists
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_FIRST_SEEN);
      if (!raw) {
        localStorage.setItem(LS_FIRST_SEEN, String(nowTs));
      }
    } catch (e) { void e; }
  }, [nowTs]);

  // Helpers to load/store maps
  const loadClosedDays = () => {
    try {
      const raw = localStorage.getItem(LS_CLOSED_DAYS);
      return raw ? JSON.parse(raw) : {};
    } catch (e) { return {}; }
  };
  const saveClosedDays = (obj) => {
    try { localStorage.setItem(LS_CLOSED_DAYS, JSON.stringify(obj)); } catch (e) { /* noop */ }
  };
  const loadVideoWeeks = () => {
    try {
      const raw = localStorage.getItem(LS_VIDEO_WEEKS);
      return raw ? JSON.parse(raw) : {};
    } catch (e) { return {}; }
  };
  const saveVideoWeeks = (obj) => {
    try { localStorage.setItem(LS_VIDEO_WEEKS, JSON.stringify(obj)); } catch (e) { /* noop */ }
  };

  const daysSinceFirstSeen = useMemo(() => {
    try {
      const fsRaw = localStorage.getItem(LS_FIRST_SEEN);
      let fs = fsRaw ? parseInt(fsRaw, 10) : nowTs;
      if (!Number.isFinite(fs)) fs = nowTs;
      // Guard: if first-seen is in the future (e.g., after timewarp reset), clamp to now and persist
      if (fs > nowTs) {
        fs = nowTs;
        try { localStorage.setItem(LS_FIRST_SEEN, String(nowTs)); } catch (e) { /* noop */ }
      }
      const a = startOfDay(nowTs);
      const b = startOfDay(fs);
      const diff = Math.floor((a - b) / MS_DAY);
      return Math.max(0, diff);
    } catch (e) { return 0; }
  }, [nowTs]);

  // Memory guard to prevent re-open on route changes within same day
  let memoryShownToday = false;
  try {
    if (typeof window !== "undefined") {
      const mark = window.__rl && window.__rl.adOverlayShownDayKey;
      memoryShownToday = mark === todayKey;
    }
  } catch (e) { /* noop */ }

  const [closedDaysMap, setClosedDaysMap] = useState(() => loadClosedDays());
  const [videoWeeksMap, setVideoWeeksMap] = useState(() => loadVideoWeeks());
  useEffect(() => { setClosedDaysMap(loadClosedDays()); }, [todayKey]);
  useEffect(() => { setVideoWeeksMap(loadVideoWeeks()); }, [weekKey]);

  // Determine whether an overlay should be shown now
  const eligibleByDay = !isSaturday && daysSinceFirstSeen >= 4; // first 4 days are ad-free
  const alreadyClosedToday = !!closedDaysMap[todayKey];
  const shouldShowNow = eligibleByDay && !alreadyClosedToday; // memoryShownToday removed to ensure first render

  // Among eligible days, decide whether this is a video day (first ad of the week)
  const isVideoDay = shouldShowNow && !videoWeeksMap[weekKey];

  // Which video to show by week parity
  const weekNumber = getSundayWeekNumber(new Date(nowTs));
  const isOddWeek = weekNumber % 2 === 1;
  const videoUrl = isOddWeek ? VIDEO_A_URL : VIDEO_B_URL;

  const [open, setOpen] = useState(shouldShowNow);
  const consentResolved = hasAnalyticsChoice();
  // Track if user dismissed today within this session to prevent immediate re-open
  const dismissedTodayRef = useRef(false);

  const [skipEnabled, setSkipEnabled] = useState(!isVideoDay); // for video: enabled after 5s
  const [countdownMs, setCountdownMs] = useState(isVideoDay ? 5000 : 0);
  const intervalRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    // Recompute seed on route change
    setSeed(Math.floor(Math.random() * 1e9));
  }, [location.pathname]);

  useEffect(() => {
    // Only open when eligibility turns true; do not auto-close if eligibility flips to false after marking as shown
    if (shouldShowNow && !open && !dismissedTodayRef.current) {
      setOpen(true);
    }
  }, [shouldShowNow, open]);

  // Force-open overlay if eligible on initial mount to avoid missing first render
  useEffect(() => {
    if (shouldShowNow) setOpen(true);
  }, [shouldShowNow]);

  useEffect(() => {
  // Debug helper: expose computed overlay state for quick verification
  try {
    if (typeof window !== "undefined") {
      window.__rl = window.__rl || {};
      window.__rl.overlayState = { eligibleByDay, alreadyClosedToday, memoryShownToday, shouldShowNow, isVideoDay, todayKey, weekKey };
    }
  } catch (e) { /* noop */ }

    // Manage body scroll lock
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);
    // Write per-session/day memory mark so route changes avoid re-open before LS updates
    // Note: do NOT set per-session memory mark here; it prevented first render in some cases

    try {
      if (typeof window !== "undefined") {
        window.__rl = window.__rl || {};
        window.__rl.adOverlayShownDayKey = todayKey;
      }
    } catch (e) { /* noop */ }


  // Mark the day as shown immediately when overlay opens (new rule)
  const shownMarkedRef = useRef(false);
  useEffect(() => {
    if (!open) return;
    if (!shouldShowNow) return;
    if (shownMarkedRef.current) return;
    shownMarkedRef.current = true;

    // Mark that an ad was shown today (prevents further ads today)
    const closed = loadClosedDays();
    if (!closed[todayKey]) {
      closed[todayKey] = true;
      saveClosedDays(closed);
      setClosedDaysMap({ ...closed });
    }
    // If this was the video day, mark the weekly video as satisfied upon showing (not just on close)
    if (isVideoDay) {
      const vw = loadVideoWeeks();
      if (!vw[weekKey]) {
        vw[weekKey] = true;
        saveVideoWeeks(vw);
        setVideoWeeksMap({ ...vw });
      }
    }
    // Removed overlay-shown broadcast per rollback request

    // Notify listeners (e.g., AdSlots) that an overlay ad has been shown
    try {
      window.dispatchEvent(new CustomEvent("rl-ad-overlay-shown", { detail: { todayKey } }));
    } catch (e) { /* noop */ }
  }, [open, shouldShowNow, isVideoDay, todayKey, weekKey]);


  useEffect(() => {
    if (!open) return;
    if (!isVideoDay) return; // countdown only for video
    setSkipEnabled(false);
    setCountdownMs(5000);
    const start = Date.now();
    const loop = () => {
      const elapsed = Date.now() - start;
      const remain = Math.max(0, 5000 - elapsed);
      setCountdownMs(remain);
      if (remain <= 0) {
        setSkipEnabled(true);
      } else {
        intervalRef.current = window.requestAnimationFrame(loop);
    // Try to ensure volume is ON (autoplay with sound may be blocked by some browsers; this attempts to enable sound once allowed)
    try {
      if (videoRef.current) {
        videoRef.current.muted = false;
        const playPromise = videoRef.current.play?.();
        if (playPromise && typeof playPromise.then === 'function') {
          playPromise.catch(() => {/* autoplay with sound blocked until user gesture */});
        }
      }
    } catch (e) { /* noop */ }

      }
    };
    intervalRef.current = window.requestAnimationFrame(loop);
    return () => { if (intervalRef.current) window.cancelAnimationFrame(intervalRef.current); };
  }, [open, isVideoDay]);

  const handleClose = () => {
    // Mark closed for today; if it was a video day, mark the week as done
    const closed = loadClosedDays();
    closed[todayKey] = true;
    saveClosedDays(closed);
    setClosedDaysMap({ ...closed });

    if (isVideoDay) {
      const vw = loadVideoWeeks();
      vw[weekKey] = true;
      saveVideoWeeks(vw);
      setVideoWeeksMap({ ...vw });
    }

    // Prevent re-open this session/day
    dismissedTodayRef.current = true;

    setOpen(false);
  };

  // Choose ad snippet
  const overlayAd = useMemo(() => {
    return pickAffiliateAd(seed, /*preferEmergent*/ isVideoDay);
  }, [seed, isVideoDay]);

  if (!consentResolved) return null;
  if (!shouldShowNow) return null;
  if (!open) return null;

  // UI helpers
  const countdownSec = Math.ceil(countdownMs / 1000);

  // Badge component: match bottom page ad badge styling and placement
  const Badge = ({ text }) => (
    <div className="absolute top-0 left-2 -translate-y-1/2 z-[1020] pointer-events-none">
      <span className="inline-flex items-center gap-1.5 font-semibold uppercase tracking-wide text-[10px] md:text-[11px] lg:text-[12px] px-2 md:px-2 lg:px-3 py-0.5 md:py-1 rounded-full bg-black text-emerald-100 ring-1 ring-emerald-500/35 shadow-[0_0_10px_rgba(16,185,129,0.2)]">
        <ShieldCheck className="h-3 w-3 md:h-3.5 md:w-3.5 lg:h-4 lg:w-4" /> {text}
      </span>
    </div>
  );

  const badgeText = isVideoDay ? 'Affiliate Ad' : (overlayAd?.labelText || 'Affiliate ad');

  return (
    <div className="fixed inset-0 z-[1000]">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      {/* Close/Skip button (top-right) */}
      <div className="absolute top-4 right-4 z-[1020] flex items-center gap-3">
        <button
          type="button"
          onClick={handleClose}
          disabled={isVideoDay && !skipEnabled}
          className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold border transition ${isVideoDay && !skipEnabled ? 'border-white/20 text-white/60 bg-white/5' : 'border-white/30 text-white bg-white/10 hover:bg-white/15'}`}
          aria-label={isVideoDay ? (skipEnabled ? 'Skip video ad' : `Skip in ${countdownSec} seconds`) : 'Close ad'}
        >
          <X className="h-4 w-4" /> {isVideoDay ? (skipEnabled ? 'Skip' : `Skip in ${countdownSec}`) : 'Close'}
        </button>
      </div>
      {/* Promo text above video (desktop only) */}
      {isVideoDay ? (
        <div className="hidden md:block absolute top-6 left-1/2 -translate-x-1/2 z-[1020] select-none">
          <div className="rounded-full bg-black/70 text-white text-xs sm:text-sm px-3 py-1 border border-white/20 shadow">
            For first 100 people, the code "TOPRAK20" gives 20% off first month
          </div>
        </div>
      ) : null}


      {/* Content */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        {isVideoDay ? (
          <div className="w-full max-w-[520px] sm:max-w-[680px] md:max-w-[820px] lg:max-w-[900px]">
            <div className="relative aspect-[9/16] w-full rounded-2xl overflow-visible border border-white/15 shadow-2xl bg-black">
              {/* Badge over the overlay content */}
              <Badge text={badgeText} />
              <a href={VIDEO_CLICK_HREF} target="_blank" rel="noopener noreferrer" className="absolute inset-0">
                <video
                  key={videoUrl}
                  ref={videoRef}
                  src={videoUrl}
                  className="absolute inset-0 w-full h-full object-contain bg-black pointer-events-none"
                  autoPlay
                  playsInline
                />
              </a>
              {/* Mobile-only promo pill that sits half-over the bottom border of the video (emergent video days) */}
              <div className="md:hidden absolute left-1/2 -translate-x-1/2 bottom-[-14px] z-[1020] select-none">
                <div className="inline-block max-w-[95%] rounded-full bg-black/80 text-white text-xs px-3 py-1 border border-white/20 shadow">
                  For first 100 people, the code "TOPRAK20" gives 20% off first month
                </div>
              </div>

              {/* Promo moved back to top (rollback) */}
            </div>
          </div>
        ) : (
          overlayAd ? (
            <div className="w-full max-w-[520px] sm:max-w-[680px] md:max-w-[820px] lg:max-w-[900px]">
              <a href={overlayAd.href} target="_blank" rel="noopener noreferrer" className="block">
                {overlayAd.emergent ? (
                  // Emergent overlays: show the full image on all breakpoints (no cropping)
                  <div className="relative w-full rounded-2xl overflow-visible border border-white/15 shadow-2xl bg-transparent">
                    {/* Badge over the overlay content */}
                    <Badge text={badgeText} />
                    {overlayAd.imageUrl ? (
                      <img
                        src={overlayAd.imageUrl}
                        alt={overlayAd.alt || overlayAd.title}
                        className="block w-full h-auto select-none"
                      />
                    ) : (
                      <div className="w-full h-[360px] bg-gradient-to-br from-teal-600 to-indigo-700" aria-hidden="true" />
                    )}
                    {/* Promo removed from emergent images (rollback) */}
                  </div>
                ) : (
                  // Non-emergent overlays: keep vertical 9:16 with object-cover to eliminate black bars
                  <div className="relative aspect-[9/16] w-full rounded-2xl overflow-visible border border-white/15 shadow-2xl bg-black">
                    {/* Badge over the overlay content */}
                    <Badge text={badgeText} />
                    {overlayAd.imageUrl ? (
                      <div className="absolute inset-0 w-full h-full">
                        <img
                          src={overlayAd.imageUrl}
                          alt={overlayAd.alt || overlayAd.title}
                          className="absolute inset-0 w-full h-full object-cover object-center select-none bg-black"
                          style={{ transform: 'none', transformOrigin: 'center center' }}
                        />
                      </div>
                    ) : (
                      <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-teal-600 to-indigo-700" aria-hidden="true" />
                    )}
                  </div>
                )}
              </a>
            </div>
          ) : (
            <div className="w-full h-full" aria-hidden="true" />
          )
        )}
      </div>
    </div>
  );
}